import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewsItem, Resource } from '../Models/NewsModels';
import './NewsAdmin.css';
import Config from '../../../config.tsx';

const ADMIN_PASSWORD = 'Saldo12012010!';

const NewsAdmin = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [passwordInput, setPasswordInput] = useState<string>('');
    const navigate = useNavigate();

    const handlePasswordSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordInput === ADMIN_PASSWORD) {
            setIsAuthenticated(true);
        } else {
            navigate('/');
        }
    };

    const [news, setNews] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [saveStatus, setSaveStatus] = useState<string | null>(null);

    // Form state
    const emptyNewsItem: NewsItem = {
        header: '',
        description: '',
        content_text: '',
        date: new Date().toISOString().split('T')[0],
        resources: []
    };

    const [formData, setFormData] = useState<NewsItem>(emptyNewsItem);
    const [newResourceName, setNewResourceName] = useState<string>('');
    const [newResourceType, setNewResourceType] = useState<'IMAGE' | 'VIDEO' | 'FILE'>('IMAGE');

    // Fetch news data
    useEffect(() => {
        const fetchNews = async () => {
            try {
                const response = await fetch(`${Config.API_URL}/news`);
                if (!response.ok) {
                    throw new Error('Problem z pobraniem danych newsów');
                }
                const data = await response.json();
                // Sort news by date (newest first)
                data.sort((a: NewsItem, b: NewsItem) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
                );
                setNews(data);
                setLoading(false);
            } catch (error) {
                console.error('Błąd podczas pobierania danych:', error);
                setError('Nie udało się pobrać danych. Sprawdź połączenie i odśwież stronę.');
                setLoading(false);
            }
        };

        fetchNews();
    }, []);

    // Save news data
    const saveNewsData = async (updatedNews: NewsItem[]) => {
        try {
            const response = await fetch(`${Config.API_URL}/news`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedNews),
            });

            if (!response.ok) {
                throw new Error('Problem z zapisem danych');
            }

            // Update local state
            setNews(updatedNews);
            setSaveStatus('Zapisano pomyślnie!');

            // Clear save status after 3 seconds
            setTimeout(() => setSaveStatus(null), 3000);

            return true;
        } catch (error) {
            console.error('Błąd podczas zapisywania danych:', error);
            setSaveStatus('Błąd zapisywania. Spróbuj ponownie.');
            setTimeout(() => setSaveStatus(null), 3000);
            return false;
        }
    };

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Add a new resource to the form
    const handleAddResource = () => {
        if (!newResourceName) return;

        const newResource: Resource = {
            name: newResourceName,
            type: newResourceType
        };

        setFormData({
            ...formData,
            resources: [...formData.resources, newResource]
        });

        setNewResourceName('');
        setNewResourceType('IMAGE');
    };

    // Remove resource from the form
    const handleRemoveResource = (index: number) => {
        const updatedResources = [...formData.resources];
        updatedResources.splice(index, 1);

        setFormData({
            ...formData,
            resources: updatedResources
        });
    };

    // Edit existing news
    const handleEditNews = (newsItem: NewsItem) => {
        setFormData(newsItem);
        setSelectedNews(newsItem);
        setIsEditing(true);
    };

    // Add new news
    const handleAddNewNews = () => {
        setFormData(emptyNewsItem);
        setSelectedNews(null);
        setIsEditing(true);
    };

    // Delete news
    const handleDeleteNews = async (newsItem: NewsItem) => {
        if (window.confirm('Czy na pewno chcesz usunąć tę wiadomość?')) {
            const updatedNews = news.filter(item => item !== newsItem);
            const success = await saveNewsData(updatedNews);

            if (success) {
                // Reset form if the deleted item was being edited
                if (selectedNews === newsItem) {
                    setFormData(emptyNewsItem);
                    setSelectedNews(null);
                    setIsEditing(false);
                }
            }
        }
    };

    // Save form data
    const handleSaveForm = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        if (!formData.header || !formData.description || !formData.content_text || !formData.date) {
            alert('Wypełnij wszystkie wymagane pola!');
            return;
        }

        let updatedNews: NewsItem[];

        if (selectedNews) {
            // Update existing news
            updatedNews = news.map(item =>
                item === selectedNews ? formData : item
            );
        } else {
            // Add new news
            updatedNews = [formData, ...news];
        }

        const success = await saveNewsData(updatedNews);

        if (success) {
            setIsEditing(false);
            setSelectedNews(null);
            setFormData(emptyNewsItem);
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setIsEditing(false);
        setSelectedNews(null);
        setFormData(emptyNewsItem);
    };

    if (!isAuthenticated) {
        return (
            <div className="news-admin-container">
                <div className="news-admin-login">
                    <h2>Panel administracyjny</h2>
                    <form onSubmit={handlePasswordSubmit}>
                        <input
                            type="password"
                            placeholder="Hasło"
                            value={passwordInput}
                            onChange={(e) => setPasswordInput(e.target.value)}
                            autoFocus
                        />
                        <button type="submit">Zaloguj</button>
                    </form>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="news-admin-loading">Ładowanie danych...</div>;
    }

    if (error) {
        return <div className="news-admin-error">{error}</div>;
    }

    return (
        <div className="news-admin-container">
            <h1>Panel administracyjny aktualności</h1>

            {saveStatus && <div className="save-status">{saveStatus}</div>}

            <div className="news-admin-layout">
                <div className="news-admin-list">
                    <div className="news-admin-header">
                        <h2>Lista aktualności</h2>
                        <button
                            className="btn-add-news"
                            onClick={handleAddNewNews}
                            disabled={isEditing}
                        >
                            Dodaj nową aktualność
                        </button>
                    </div>

                    {news.length === 0 ? (
                        <p>Brak aktualności do wyświetlenia.</p>
                    ) : (
                        <ul className="news-list">
                            {news.map((item, index) => (
                                <li key={index} className="news-list-item">
                                    <div className="news-list-content">
                                        <h3>{item.header}</h3>
                                        <p className="news-date">{new Date(item.date).toLocaleDateString('pl-PL')}</p>
                                        <p className="news-description">{item.description}</p>
                                    </div>
                                    <div className="news-list-actions">
                                        <button
                                            className="btn-edit"
                                            onClick={() => handleEditNews(item)}
                                            disabled={isEditing}
                                        >
                                            Edytuj
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => handleDeleteNews(item)}
                                            disabled={isEditing}
                                        >
                                            Usuń
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {isEditing && (
                    <div className="news-admin-form">
                        <h2>{selectedNews ? 'Edytuj aktualność' : 'Dodaj nową aktualność'}</h2>

                        <form onSubmit={handleSaveForm}>
                            <div className="form-group">
                                <label htmlFor="header">Tytuł*:</label>
                                <input
                                    type="text"
                                    id="header"
                                    name="header"
                                    value={formData.header}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="date">Data*:</label>
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Krótki opis*:</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="content_text">Pełna treść*:</label>
                                <textarea
                                    id="content_text"
                                    name="content_text"
                                    value={formData.content_text}
                                    onChange={handleInputChange}
                                    rows={8}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Zasoby:</label>
                                <div className="resources-list">
                                    {formData.resources.map((resource, index) => (
                                        <div key={index} className="resource-item">
                                            <span className="resource-name">{resource.name}</span>
                                            <span className="resource-type">{resource.type}</span>
                                            <button
                                                type="button"
                                                className="btn-remove-resource"
                                                onClick={() => handleRemoveResource(index)}
                                            >
                                                Usuń
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="add-resource-form">
                                    <input
                                        type="text"
                                        placeholder="Nazwa zasobu"
                                        value={newResourceName}
                                        onChange={(e) => setNewResourceName(e.target.value)}
                                    />
                                    <select
                                        value={newResourceType}
                                        onChange={(e) => setNewResourceType(e.target.value as 'IMAGE' | 'VIDEO' | 'FILE')}
                                    >
                                        <option value="IMAGE">Obraz</option>
                                        <option value="VIDEO">Wideo</option>
                                        <option value="FILE">Plik</option>
                                    </select>
                                    <button
                                        type="button"
                                        className="btn-add-resource"
                                        onClick={handleAddResource}
                                    >
                                        Dodaj
                                    </button>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn-save">Zapisz</button>
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={handleCancelEdit}
                                >
                                    Anuluj
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewsAdmin;
